//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import * as Css from 'csstype';
import React from 'react';
import styled, { css } from 'styled-components';

import { Mixins, TextMixin } from '@theme/mixins';

// This is specific to the sofia-pro typeface and should
// not be used for other typefaces.
export const CENTER_OFFSET = -0.1;

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  mixin: TextMixin;
  ellipsize?: number;
  height?: number;
  justifyContent?: 'flex-start' | 'flex-end' | 'center';
  lineHeight?: number;
  offset?: number;
  trimWhitespaceBottom?: number;
  trimWhitespaceTop?: number;
  wordBreak?: Css.Property.WordBreak;
  whiteSpace?: Css.Property.WhiteSpace;
  textRef?: React.RefObject<HTMLDivElement>;
}

/**
 * The Text atom renders one or more lines of text. It is built to ensure
 * a few key things...
 * 1. Typeface-specific features like ascenders and descenders are never clipped (e.g. the
 *    bottom of a 'g' or 'y' character, or the top of '|').
 * 2. The consumer has tight control over the footprint of the text allowing
 *    fine-grained positioning. Features like whitespace and how it is distributed
 *    can be easily managed with minimal configuration.
 * 3. The footprint occupied by the text is not dependent on typeface-specific
 *    features like whitespace.
 *
 * While this atom works in all instances that require text to be rendered, the added boilerplate
 * here means it should be used most often in other atoms that require one or more lines of
 * text to be rendered.
 *
 * @param mixin A value from the Mixins.Text object, used to style the text
 * @param ellipsize If greater than zero, ellipsize the text after so many lines
 * @param height The height of the box, in pixels, that will contain the text. This box will never clip characters
 * @param justifyContent The subset of 'flex-start' or 'flex-end' of the 'justify-content' CSS rule of the div wrapping the text (can be used to align right or left)
 * @param lineHeight The line-height of the text. This should generally be left at 1.5 to ensure the text
 *   itself has enough space to render without being clipped
 * @param offset The relative position of the text, in ems, within the containing box
 * @param trimWhitespaceTop The amount of whitespace, in ems, to remove from the top of the text node
 * @param trimWhitespaceBottom See above
 * @param wordBreak The 'word-break' CSS rule used when ellipsizing
 * @param whiteSpace The 'white-space' CSS rule used for preserving line breaks
 *
 * @example
 * // Renders two lines of ellipsized text stacked closely on top of one another
 * // without clipping any characters. The containing div will be 28px tall even though
 * // each text node is 21px tall.
 * <div>
 *   <Text mixin={ Mixins.Text.Body } ellipsize={ 1 } height={ 14 }>This is the first line</Text>
 *   <Text mixin={ Mixins.Text.Body } ellipsize={ 1 } height={ 14 }>This is the second line</Text>
 * </div>
 */
export const Text = ({
  children,
  className,
  ellipsize = 0,
  height,
  justifyContent = 'flex-start',
  lineHeight = 1.5,
  mixin,
  offset = CENTER_OFFSET,
  textRef,
  trimWhitespaceBottom = 0,
  trimWhitespaceTop = 0,
  whiteSpace,
  wordBreak = 'break-word',
  ...divProps
}: Props) => {
  return (
    <StyledTextLine
      $height={height}
      $justifyContent={justifyContent}
      $mixin={mixin}
      ref={textRef}
    >
      <StyledText
        {...divProps}
        $ellipsize={ellipsize}
        $lineHeight={lineHeight}
        $mixin={mixin}
        $offset={offset}
        $trimWhitespaceBottom={trimWhitespaceBottom}
        $trimWhitespaceTop={trimWhitespaceTop}
        $whiteSpace={whiteSpace}
        $wordBreak={wordBreak}
        className={className}
      >
        {children}
      </StyledText>
    </StyledTextLine>
  );
};

const StyledTextLine = styled.div<{
  $height?: number;
  $justifyContent: 'flex-start' | 'flex-end' | 'center';
  $mixin: TextMixin;
}>(
  ({ $height, $justifyContent, $mixin }) => css`
    display: flex;
    align-items: center;
    justify-content: ${$justifyContent};
    overflow: visible;
    ${$mixin};
    ${$height ? `height: ${$height}px !important;` : ''};
  `,
);

const StyledText = styled.div<{
  $ellipsize?: number;
  $lineHeight?: number;
  $offset?: number;
  $trimWhitespaceTop?: number;
  $trimWhitespaceBottom?: number;
  $wordBreak?: Css.Property.WordBreak;
  $whiteSpace?: Css.Property.WhiteSpace;
  $mixin: TextMixin;
}>(
  ({
    $ellipsize,
    $lineHeight,
    $offset,
    $trimWhitespaceBottom,
    $trimWhitespaceTop,
    $whiteSpace,
    $wordBreak,
  }) => css`
    line-height: ${$lineHeight};
    position: relative;
    ${$whiteSpace ? `white-space: ${$whiteSpace};` : ''};
    ${$offset ? `top: calc(1em * ${$offset});` : ''};
    ${$ellipsize
      ? css`
          -webkit-line-clamp: ${$ellipsize};
          -webkit-box-orient: vertical;
          display: -webkit-box;
          overflow: hidden;
          text-overflow: ellipsis;
          word-break: ${$wordBreak};
        `
      : ''};
    ${$trimWhitespaceTop || $trimWhitespaceBottom
      ? css`
          &::before,
          &::after {
            content: '';
            display: block;
            height: 0;
            width: 0;
          }
          &::before {
            margin-top: -${$trimWhitespaceTop}em;
          }
          &::after {
            margin-top: -${$trimWhitespaceBottom}em;
          }
        `
      : ''};
  `,
);

type PrestyledProps = Omit<Props, 'mixin'>;

export const BodyBoldText = (props: PrestyledProps) => (
  <Text mixin={Mixins.Text.BodyBold} {...props} />
);
export const BodyText = (props: PrestyledProps) => (
  <Text mixin={Mixins.Text.Body} {...props} />
);
export const BodySecondaryText = (props: PrestyledProps) => (
  <Text mixin={Mixins.Text.BodySecondary} {...props} />
);
export const BodyDisabledText = (props: PrestyledProps) => (
  <Text mixin={Mixins.Text.BodyDisabled} {...props} />
);
export const BodyErrorText = (props: PrestyledProps) => (
  <Text mixin={Mixins.Text.BodyError} {...props} />
);
export const CaptionSecondaryText = (props: PrestyledProps) => (
  <Text mixin={Mixins.Text.CaptionSecondary} {...props} />
);
export const CaptionSecondaryDisabledText = (props: PrestyledProps) => (
  <Text mixin={Mixins.Text.CaptionSecondaryDisabled} {...props} />
);
export const CaptionText = (props: PrestyledProps) => (
  <Text mixin={Mixins.Text.Caption} {...props} />
);
export const CaptionErrorText = (props: PrestyledProps) => (
  <Text mixin={Mixins.Text.CaptionError} {...props} />
);
export const LabelText = (props: PrestyledProps) => (
  <Text lineHeight={1} mixin={Mixins.Text.Label} {...props} />
);
export const LabelDisabledText = (props: PrestyledProps) => (
  <Text lineHeight={1} mixin={Mixins.Text.LabelDisabled} {...props} />
);
export const LinkCaptionPrimaryText = (props: PrestyledProps) => (
  <Text mixin={Mixins.Text.LinkCaptionPrimary} {...props} />
);
export const LinkPrimaryText = (props: PrestyledProps) => (
  <Text mixin={Mixins.Text.LinkPrimary} {...props} />
);
export const MetatagText = (props: PrestyledProps) => (
  <Text mixin={Mixins.Text.Metatag} {...props} />
);
export const SectionText = (props: PrestyledProps) => (
  <Text mixin={Mixins.Text.Section} {...props} />
);
export const SectionBoldText = (props: PrestyledProps) => (
  <Text mixin={Mixins.Text.SectionBold} {...props} />
);
export const SubsectionText = (props: PrestyledProps) => (
  <Text mixin={Mixins.Text.Subsection} {...props} />
);
export const SubtitleText = (props: PrestyledProps) => (
  <Text mixin={Mixins.Text.Subtitle} {...props} />
);
export const TableHeaderCellText = (props: PrestyledProps) => (
  <Text mixin={Mixins.Text.TableHeaderCell} {...props} />
);
export const TableDataPrimaryText = (props: PrestyledProps) => (
  <Text mixin={Mixins.Text.TableDataPrimary} {...props} />
);
export const TableDataSecondaryText = (props: PrestyledProps) => (
  <Text mixin={Mixins.Text.TableDataSecondary} {...props} />
);
export const TitleText = (props: PrestyledProps) => (
  <Text mixin={Mixins.Text.Title} {...props} />
);
export const UiCopySecondaryText = (props: PrestyledProps) => (
  <Text mixin={Mixins.Text.UiCopySecondary} {...props} />
);
export const UiCopyText = (props: PrestyledProps) => (
  <Text mixin={Mixins.Text.UiCopy} {...props} />
);
export const UiCopyBoldText = (props: PrestyledProps) => (
  <Text mixin={Mixins.Text.UiCopyBold} {...props} />
);

export default Text;
